import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Files, Printer } from "react-bootstrap-icons";
import ClassStudentPicker from "../EOYReportAssistant/ClassStudentPicker";
import {
  buildClipboardPayload,
  writeClipboard,
} from "../EOYReportAssistant/ReportCard";
import ScoresVisibilityToggle from "../EOYReportAssistant/ScoresVisibilityToggle";
import { printReportNodes } from "../EOYReportAssistant/printReports";
import NewClassInsightsReportCard from "./NewClassInsightsReportCard";
import useEnhancedReportWorkflow from "./useEnhancedReportWorkflow";
import useAcademicYearOptions from "../../utils/useAcademicYearOptions";
import { getStudentDisplayName } from "./enhancedReportsUtils";
import {
  generateBatchNewClassInsights,
  getNewClassInsightsClassReports,
} from "../../services/APIClient";

const NCI_GRADE_LEVEL_OPTIONS = [
  { value: "senior", label: "Senior Infants" },
  { value: "first", label: "1st Class" },
];

export function NewClassInsightsBulkToolbar({
  allScoresVisible,
  allSummariesEdited,
  bulkCopyStatus,
  onCopyAllReports,
  onPrintAllReports,
  onScoresVisibilityChange,
  onSummaryModeChange,
  reportCount,
}) {
  return (
    <div className="eoy-sticky-panel eoy-bulk-toolbar no-print">
      <div className="eoy-bulk-toolbar-main">
        <span className="eoy-bulk-toolbar-title">Work with all reports</span>
        <span className="eoy-bulk-toolbar-meta">
          {reportCount} report{reportCount === 1 ? "" : "s"} ready
        </span>
      </div>
      <div className="eoy-bulk-toolbar-actions">
        <span
          className={`eoy-copy-status ${
            bulkCopyStatus ? "" : "eoy-copy-status-hidden"
          }`}
          aria-live="polite"
        >
          {bulkCopyStatus || "Copied all"}
        </span>
        <div className="eoy-bulk-toolbar-section">
          <div className="eoy-bulk-summary-toggle">
            <span className="eoy-summary-toggle-label">AI Generated</span>
            <label
              className="eoy-summary-switch"
              data-tooltip="Switch all New Class Insights between generated and edited text"
            >
              <input
                type="checkbox"
                aria-label="Switch all New Class Insights between generated and edited text"
                checked={allSummariesEdited}
                onChange={(event) =>
                  onSummaryModeChange(
                    event.target.checked ? "edited" : "generated",
                  )
                }
              />
              <span className="eoy-summary-slider" />
            </label>
            <span className="eoy-summary-toggle-label">Edited</span>
          </div>
        </div>
        <div className="eoy-bulk-toolbar-section">
          <ScoresVisibilityToggle
            checked={allScoresVisible}
            onChange={onScoresVisibilityChange}
          />
        </div>
        <button
          aria-label="Copy all New Class Insights to clipboard"
          className="eoy-toolbar-icon-btn eoy-bulk-action-btn"
          onClick={onCopyAllReports}
          data-tooltip="Copy all New Class Insights to clipboard"
          type="button"
        >
          <Files aria-hidden="true" size={15} />
        </button>
        <button
          aria-label="Print all New Class Insights"
          className="eoy-toolbar-icon-btn eoy-bulk-action-btn"
          onClick={onPrintAllReports}
          data-tooltip="Print all New Class Insights"
          type="button"
        >
          <Printer aria-hidden="true" size={15} />
        </button>
      </div>
    </div>
  );
}

/**
 * New Class Insights tab. Shares class/student selection state (`selection`,
 * from useClassSelection) with the other Enhanced AI Report tabs; owns its
 * own report generation state via useEnhancedReportWorkflow.
 *
 * This is an incoming-teacher handover tool, not a generic report explorer,
 * so it deliberately has no year picker: the source (previous) and
 * destination (current) academic years are derived automatically from
 * today's date, the same way `useClassSelection`'s default "current year"
 * class list already works for this tab.
 */
function NewClassInsightsTab({ selection, title, subheading }) {
  const [summaryModeByStudent, setSummaryModeByStudent] = useState({});
  const [scoresVisibleByStudent, setScoresVisibleByStudent] = useState({});
  const [bulkCopyStatus, setBulkCopyStatus] = useState("");
  const reportNodeRefs = useRef(new Map());
  const activePrintCleanupRef = useRef(null);
  const bulkCopyStatusTimerRef = useRef(null);
  // Ordered [current, previous].
  const yearOptions = useAcademicYearOptions({ previousYears: 1 });
  const destinationAcademicYear = yearOptions[0]?.storage || "";
  const sourceAcademicYear = yearOptions[1]?.storage || "";

  const extraFields = useMemo(
    () => ({ sourceAcademicYear, destinationAcademicYear }),
    [sourceAcademicYear, destinationAcademicYear]
  );
  const extraFieldsKey = `${sourceAcademicYear}|${destinationAcademicYear}`;

  const buildStudentPayload = (studentId) => ({
    studentId,
    sourceAcademicYear,
    destinationAcademicYear,
  });

  const workflow = useEnhancedReportWorkflow({
    generateBatch: generateBatchNewClassInsights,
    getClassReports: getNewClassInsightsClassReports,
    buildStudentPayload,
    extraFields,
  });
  const successfulReports = useMemo(
    () => workflow.reports.filter((report) => report.success),
    [workflow.reports],
  );

  useEffect(() => {
    const reportIds = new Set(
      successfulReports.map((report) => report.student_id),
    );
    setSummaryModeByStudent((current) => {
      const next = {};
      successfulReports.forEach((report) => {
        next[report.student_id] =
          current[report.student_id] ||
          (String(report.edited_report || "").trim()
            ? "edited"
            : "generated");
      });
      return next;
    });
    setScoresVisibleByStudent((current) => {
      const next = {};
      reportIds.forEach((studentId) => {
        next[studentId] = current[studentId] ?? true;
      });
      return next;
    });
  }, [successfulReports]);

  useEffect(
    () => () => {
      activePrintCleanupRef.current?.();
      if (bulkCopyStatusTimerRef.current) {
        window.clearTimeout(bulkCopyStatusTimerRef.current);
      }
    },
    [],
  );

  const handleReportNodeReady = useCallback((studentId, node) => {
    if (!studentId) return;
    if (node) reportNodeRefs.current.set(studentId, node);
    else reportNodeRefs.current.delete(studentId);
  }, []);

  const getSuccessfulReportNodes = useCallback(
    () =>
      successfulReports
        .map((report) => reportNodeRefs.current.get(report.student_id))
        .filter(Boolean),
    [successfulReports],
  );

  const handlePrintAllReports = useCallback(() => {
    const nodes = getSuccessfulReportNodes();
    if (nodes.length === 0) return;
    activePrintCleanupRef.current?.();
    activePrintCleanupRef.current = printReportNodes(nodes);
  }, [getSuccessfulReportNodes]);

  const handleCopyAllReports = useCallback(async () => {
    const payloads = getSuccessfulReportNodes()
      .map((node) => buildClipboardPayload(node))
      .filter(Boolean);
    const payload = payloads.length
      ? {
          html: payloads
            .map((item, index) =>
              index === 0
                ? item.html
                : `<div style="break-before: page; page-break-before: always;">&nbsp;</div>${item.html}`,
            )
            .join(""),
          text: payloads.map((item) => item.text).join("\n\n\f\n\n"),
        }
      : null;

    try {
      const copied = await writeClipboard(payload);
      setBulkCopyStatus(copied ? "Copied all" : "Copy failed");
    } catch (error) {
      console.warn("Could not copy all New Class Insights:", error);
      setBulkCopyStatus("Copy failed");
    }
    if (bulkCopyStatusTimerRef.current) {
      window.clearTimeout(bulkCopyStatusTimerRef.current);
    }
    bulkCopyStatusTimerRef.current = window.setTimeout(
      () => setBulkCopyStatus(""),
      1800,
    );
  }, [getSuccessfulReportNodes]);

  const allSummariesEdited =
    successfulReports.length > 0 &&
    successfulReports.every(
      (report) => summaryModeByStudent[report.student_id] === "edited",
    );
  const allScoresVisible =
    successfulReports.length > 0 &&
    successfulReports.every(
      (report) => scoresVisibleByStudent[report.student_id] !== false,
    );

  const handleBulkSummaryModeChange = (nextMode) => {
    setSummaryModeByStudent((current) => {
      const next = { ...current };
      successfulReports.forEach((report) => {
        next[report.student_id] = nextMode;
      });
      return next;
    });
  };

  const handleBulkScoresVisibilityChange = (isVisible) => {
    setScoresVisibleByStudent((current) => {
      const next = { ...current };
      successfulReports.forEach((report) => {
        next[report.student_id] = isVisible;
      });
      return next;
    });
  };

  useEffect(() => {
    workflow.resetReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.selectedClassId, extraFieldsKey]);

  useEffect(() => {
    if (!selection.selectedClassId || selection.studentsLoading) return;
    const ids = selection.students.map((student) => student.id).filter(Boolean);
    if (ids.length > 0) workflow.loadSavedReports(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selection.selectedClassId,
    selection.studentsLoading,
    selection.students,
    extraFieldsKey,
  ]);

  const handleGenerate = () => {
    if (!selection.disclaimerAccepted) return;

    const selectedStudents = selection.selectedStudentIds
      .filter((id) => !workflow.doneStudentIds.includes(id))
      .map((id) => {
        const student = selection.students.find((s) => s.id === id);
        return { student_id: id, display_name: getStudentDisplayName(student, id) };
      });

    if (selectedStudents.length === 0) return;
    workflow.generate(selectedStudents);
  };

  return (
    <div className="eoy-panel-root">
      <div className="eoy-header">
        <h1 className="eoy-title">{title}</h1>
        <p className="eoy-subtitle">{subheading}</p>
      </div>

      {!selection.classesLoading &&
        !selection.classesError &&
        selection.classes.length === 0 && (
          <div className="eoy-empty-state">
            You are not currently assigned as classroom teacher for any
            classrooms this year.
          </div>
        )}

      <ClassStudentPicker
        classes={selection.filteredClasses}
        classesLoading={selection.classesLoading}
        classesError={selection.classesError}
        selectedGradeLevel={selection.selectedGradeLevel}
        onGradeLevelChange={selection.handleGradeLevelChange}
        gradeLevelOptions={NCI_GRADE_LEVEL_OPTIONS}
        gradeLevelPrompt="Senior Infants or 1st Class"
        selectedClassId={selection.selectedClassId}
        onClassChange={selection.handleClassChange}
        students={selection.students}
        studentsLoading={selection.studentsLoading}
        studentsError={selection.studentsError}
        selectedStudentIds={selection.selectedStudentIds}
        unavailableStudentIds={workflow.doneStudentIds}
        onStudentToggle={(id) =>
          selection.handleStudentToggle(id, workflow.doneStudentIds)
        }
        onSelectAll={() => selection.handleSelectAll(workflow.doneStudentIds)}
        disclaimerAccepted={selection.disclaimerAccepted}
        onDisclaimerAcceptedChange={selection.setDisclaimerAccepted}
        onGenerate={handleGenerate}
        generating={workflow.generating || workflow.savedReportsLoading}
      />

      {workflow.savedReportsLoading && selection.selectedClassId && (
        <div className="eoy-muted eoy-saved-reports-status">
          Checking for saved reports...
        </div>
      )}

      {workflow.generating && workflow.progress.total > 0 && (
        <div className="eoy-progress-meta">
          Generating report{" "}
          {Math.min(workflow.progress.done + 1, workflow.progress.total)} of{" "}
          {workflow.progress.total}...
        </div>
      )}

      {!workflow.generating && workflow.reports.length > 0 && (
        <div className="eoy-done-summary">
          <strong>
            {workflow.reportsSource === "saved"
              ? `${workflow.reports.length} saved report${
                  workflow.reports.length === 1 ? "" : "s"
                } loaded`
              : `${workflow.reports.filter((r) => r.success).length} of ${
                  workflow.reports.length
                } reports generated`}
          </strong>
          {selection.selectedClassName && <span> for {selection.selectedClassName}</span>}
        </div>
      )}

      {workflow.genError && <div className="eoy-error">{workflow.genError}</div>}

      {!workflow.generating && successfulReports.length > 0 && (
        <NewClassInsightsBulkToolbar
          allScoresVisible={allScoresVisible}
          allSummariesEdited={allSummariesEdited}
          bulkCopyStatus={bulkCopyStatus}
          onCopyAllReports={handleCopyAllReports}
          onPrintAllReports={handlePrintAllReports}
          onScoresVisibilityChange={handleBulkScoresVisibilityChange}
          onSummaryModeChange={handleBulkSummaryModeChange}
          reportCount={successfulReports.length}
        />
      )}

      {workflow.reports.map((report) => (
        <NewClassInsightsReportCard
          key={report.student_id}
          report={report}
          className={selection.selectedClassName}
          onRegenerateReport={workflow.regenerate}
          regenerating={workflow.generating}
          onReportNodeReady={handleReportNodeReady}
          summaryMode={summaryModeByStudent[report.student_id]}
          onSummaryModeChange={(nextMode) =>
            setSummaryModeByStudent((current) => ({
              ...current,
              [report.student_id]: nextMode,
            }))
          }
          scoresVisible={scoresVisibleByStudent[report.student_id]}
          onScoresVisibilityChange={(isVisible) =>
            setScoresVisibleByStudent((current) => ({
              ...current,
              [report.student_id]: isVisible,
            }))
          }
        />
      ))}
    </div>
  );
}

export default NewClassInsightsTab;
